
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Instagram, 
  Facebook, 
  Twitter, 
  Linkedin, 
  Globe, 
  Youtube,
  Check,
  ExternalLink
} from "lucide-react";
import { Badge } from '@/components/ui/badge';

export const SocialConnections = () => {
  const { t } = useTranslation();
  const [connected, setConnected] = useState({
    instagram: true,
    facebook: true,
    twitter: false,
    linkedin: false,
    website: true,
    youtube: false
  });

  const handleConnect = (platform: keyof typeof connected) => {
    setConnected({
      ...connected,
      [platform]: !connected[platform]
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          {t('settings.socialConnections')}
        </CardTitle>
        <CardDescription>
          {t('settings.manageSocialMedia')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Instagram */}
          <div className="flex items-start justify-between space-x-4 p-4 border rounded-lg">
            <div className="flex items-center space-x-3">
              <Instagram className="h-8 w-8 text-pink-500" />
              <div>
                <h3 className="font-medium">Instagram</h3>
                {connected.instagram ? (
                  <Badge className="mt-1 bg-green-100 text-green-800 hover:bg-green-100">
                    <Check className="mr-1 h-3 w-3" />
                    {t('settings.connected')}
                  </Badge>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('settings.notConnected')}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {connected.instagram && (
                <Button size="sm" variant="outline">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  {t('settings.visit')}
                </Button>
              )}
              <Button 
                size="sm" 
                variant={connected.instagram ? "outline" : "default"}
                onClick={() => handleConnect('instagram')}
              >
                {connected.instagram ? t('settings.disconnect') : t('settings.connect')}
              </Button>
            </div>
          </div>

          {/* Facebook */}
          <div className="flex items-start justify-between space-x-4 p-4 border rounded-lg">
            <div className="flex items-center space-x-3">
              <Facebook className="h-8 w-8 text-blue-600" />
              <div>
                <h3 className="font-medium">Facebook</h3>
                {connected.facebook ? (
                  <Badge className="mt-1 bg-green-100 text-green-800 hover:bg-green-100">
                    <Check className="mr-1 h-3 w-3" />
                    {t('settings.connected')}
                  </Badge>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('settings.notConnected')}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {connected.facebook && (
                <Button size="sm" variant="outline">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  {t('settings.visit')}
                </Button>
              )}
              <Button 
                size="sm" 
                variant={connected.facebook ? "outline" : "default"}
                onClick={() => handleConnect('facebook')}
              >
                {connected.facebook ? t('settings.disconnect') : t('settings.connect')}
              </Button>
            </div>
          </div>

          {/* Twitter/X */}
          <div className="flex items-start justify-between space-x-4 p-4 border rounded-lg">
            <div className="flex items-center space-x-3">
              <Twitter className="h-8 w-8 text-blue-400" />
              <div>
                <h3 className="font-medium">Twitter/X</h3>
                {connected.twitter ? (
                  <Badge className="mt-1 bg-green-100 text-green-800 hover:bg-green-100">
                    <Check className="mr-1 h-3 w-3" />
                    {t('settings.connected')}
                  </Badge>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('settings.notConnected')}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {connected.twitter && (
                <Button size="sm" variant="outline">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  {t('settings.visit')}
                </Button>
              )}
              <Button 
                size="sm" 
                variant={connected.twitter ? "outline" : "default"}
                onClick={() => handleConnect('twitter')}
              >
                {connected.twitter ? t('settings.disconnect') : t('settings.connect')}
              </Button>
            </div>
          </div>

          {/* LinkedIn */}
          <div className="flex items-start justify-between space-x-4 p-4 border rounded-lg">
            <div className="flex items-center space-x-3">
              <Linkedin className="h-8 w-8 text-blue-700" />
              <div>
                <h3 className="font-medium">LinkedIn</h3>
                {connected.linkedin ? (
                  <Badge className="mt-1 bg-green-100 text-green-800 hover:bg-green-100">
                    <Check className="mr-1 h-3 w-3" />
                    {t('settings.connected')}
                  </Badge>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('settings.notConnected')}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {connected.linkedin && (
                <Button size="sm" variant="outline">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  {t('settings.visit')}
                </Button>
              )}
              <Button 
                size="sm" 
                variant={connected.linkedin ? "outline" : "default"}
                onClick={() => handleConnect('linkedin')}
              >
                {connected.linkedin ? t('settings.disconnect') : t('settings.connect')}
              </Button>
            </div>
          </div>

          {/* Website */}
          <div className="flex items-start justify-between space-x-4 p-4 border rounded-lg">
            <div className="flex items-center space-x-3">
              <Globe className="h-8 w-8 text-gray-600" />
              <div>
                <h3 className="font-medium">{t('settings.website')}</h3>
                {connected.website ? (
                  <Badge className="mt-1 bg-green-100 text-green-800 hover:bg-green-100">
                    <Check className="mr-1 h-3 w-3" />
                    {t('settings.connected')}
                  </Badge>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('settings.notConnected')}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {connected.website && (
                <Button size="sm" variant="outline">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  {t('settings.visit')}
                </Button>
              )}
              <Button 
                size="sm" 
                variant={connected.website ? "outline" : "default"}
                onClick={() => handleConnect('website')}
              >
                {connected.website ? t('settings.disconnect') : t('settings.connect')}
              </Button>
            </div>
          </div>

          {/* YouTube */}
          <div className="flex items-start justify-between space-x-4 p-4 border rounded-lg">
            <div className="flex items-center space-x-3">
              <Youtube className="h-8 w-8 text-red-600" />
              <div>
                <h3 className="font-medium">YouTube</h3>
                {connected.youtube ? (
                  <Badge className="mt-1 bg-green-100 text-green-800 hover:bg-green-100">
                    <Check className="mr-1 h-3 w-3" />
                    {t('settings.connected')}
                  </Badge>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('settings.notConnected')}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {connected.youtube && (
                <Button size="sm" variant="outline">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  {t('settings.visit')}
                </Button>
              )}
              <Button 
                size="sm" 
                variant={connected.youtube ? "outline" : "default"}
                onClick={() => handleConnect('youtube')}
              >
                {connected.youtube ? t('settings.disconnect') : t('settings.connect')}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
